import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/konzepte", label: "Konzepte" },
  { to: "/stilserien", label: "Stilserien" },
  { to: "/refit", label: "Refit" },
  { to: "/individualisierung", label: "Individualisierung" },
  { to: "/konfigurator", label: "Konfigurator", emphasis: true },
  { to: "/shop", label: "Shop", emphasis: true },
  { to: "/gewerblich", label: "Gewerblich" },
  { to: "/broschuere", label: "Broschüre" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const progressRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      const ratio = total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${ratio})`;
      }
      setScrolled(window.scrollY > 16);
      raf = 0;
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Determine if an emphasised pill nav item is active.
  const isEmphasisActive = (to) => {
    if (to === "/shop") return pathname.startsWith("/shop");
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-700 ${
        scrolled
          ? "bg-[color:var(--livinora-paper)]/90 backdrop-blur-xl border-b border-[color:var(--livinora-border)] shadow-[0_8px_24px_-22px_rgba(0,0,0,0.25)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-livinora flex items-center justify-between py-6 md:py-7">
        {/* Wordmark */}
        <Link to="/" className="flex items-baseline gap-3 group" data-testid="nav-logo">
          <span
            className="text-[20px] sm:text-[22px] uppercase tracking-[0.32em] leading-none font-medium text-[color:var(--livinora-ink)] group-hover:opacity-70 transition-opacity duration-500"
            data-testid="nav-wordmark"
          >
            LIVINORA
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Hauptnavigation">
          {NAV.map((item) => {
            const testId = `nav-${item.label.toLowerCase().replace(/\s|ü/g, (c) => (c === "ü" ? "ue" : "-"))}`;

            if (item.emphasis) {
              const active = isEmphasisActive(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={testId}
                  className={() =>
                    [
                      "nav-pill text-[11px] tracking-[0.2em] uppercase px-3.5 py-2 border transition-colors duration-500",
                      active
                        ? "is-active bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] border-[color:var(--livinora-ink)]"
                        : "bg-transparent text-[color:var(--livinora-ink)] border-[color:var(--livinora-ink)] hover:bg-[color:var(--livinora-ink)] hover:text-[color:var(--livinora-paper)]",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={testId}
                className={({ isActive }) =>
                  `nav-link text-[11px] tracking-[0.2em] uppercase transition-colors duration-500 ${
                    isActive
                      ? "text-[color:var(--livinora-ink)] is-active"
                      : "text-[color:var(--livinora-muted)] hover:text-[color:var(--livinora-ink)]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* CTA + mobile toggle */}
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/kontakt"
            data-testid="nav-cta-inquiry"
            className="hidden md:inline-flex btn-livinora btn-primary whitespace-nowrap"
          >
            Projekt anfragen
          </Link>
          {/* Mobile-only compact Shop pill — direct access from header band */}
          <Link
            to="/shop"
            data-testid="nav-mobile-shop"
            aria-label="Zum Shop"
            aria-current={pathname.startsWith("/shop") ? "page" : undefined}
            className="lg:hidden inline-flex items-center h-10 px-3.5 text-[11px] uppercase tracking-[0.22em] bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] border border-[color:var(--livinora-ink)] transition-opacity duration-300 active:opacity-80"
          >
            Shop
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[color:var(--livinora-border)] transition-colors hover:border-[color:var(--livinora-ink)]"
            aria-label="Menü öffnen"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Scroll progress indicator */}
      <span ref={progressRef} className="scroll-progress" aria-hidden />

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] bg-[color:var(--livinora-paper)] border-t border-[color:var(--livinora-border)] ${
          open ? "max-h-[100vh]" : "max-h-0"
        }`}
        data-testid="nav-mobile-panel"
        aria-hidden={!open}
      >
        <div className="container-livinora py-10 flex flex-col">
          <span className="overline mb-8">Navigation</span>
          <nav className="flex flex-col" aria-label="Mobile Navigation">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group flex items-baseline justify-between border-b border-[color:var(--livinora-border)] py-5 transition-colors duration-500 ${
                    isActive || (item.emphasis && isEmphasisActive(item.to))
                      ? "text-[color:var(--livinora-ink)]"
                      : "text-[color:var(--livinora-ink-soft)]"
                  }`
                }
              >
                <span className="flex items-baseline gap-4">
                  <span className="number-tag w-8 shrink-0">0{i + 1}</span>
                  <span className="serif text-3xl">
                    {item.label}
                    {item.emphasis && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--livinora-muted)] font-sans align-middle">
                        ·
                      </span>
                    )}
                  </span>
                </span>
                <span className="text-[color:var(--livinora-muted)] transition-transform duration-500 group-hover:translate-x-1">→</span>
              </NavLink>
            ))}
          </nav>

          <Link to="/kontakt" className="btn-livinora btn-primary mt-10 w-full justify-between">
            <span>Projekt anfragen</span>
            <span>→</span>
          </Link>

          <div className="mt-12 grid grid-cols-1 gap-3 text-xs uppercase tracking-[0.2em] text-[color:var(--livinora-muted)]">
            <div>
              <div className="overline">Kontakt</div>
              <Link
                to="/kontakt"
                className="mt-3 block normal-case tracking-normal text-[color:var(--livinora-ink)] text-sm underline-offset-4 hover:underline"
              >
                Kontaktformular →
              </Link>
              <Link
                to="/impressum"
                className="mt-2 block normal-case tracking-normal text-[color:var(--livinora-muted)] text-sm underline-offset-4 hover:underline"
              >
                Pflichtangaben im Impressum
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
